import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
  UserPlusIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import ConfirmationModal from './ConfirmationModal';
import { ModalShell, ModalActions, ModalSection } from './modals/core';
import AttributeLabel from './AttributeLabel';

export default function Register() {
  const location = useLocation();
  const navigate = useNavigate();

  // API URL direkt aus Environment oder Fallback
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  // URL-Parameter für Referral-Tracking und Club-ID
  const queryParams = new URLSearchParams(location.search);
  const referralSource = queryParams.get('ref') || 'direct';
  const fromDomain = queryParams.get('from_domain');
  const campaign = queryParams.get('campaign');
  const clubId = queryParams.get('club') || process.env.REACT_APP_CLUB_REGISTER_ID;

  // Form State
  const [form, setForm] = useState({
    vorname: '',
    name: '',
    geb_am: '',
    gender: '',
    strasse: '',
    plz: '',
    ort: '',
    email: '',
    password: '',
    confirmPassword: '',
    privacyConsent: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [confirmModal, setConfirmModal] = useState<any>(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [privacyData, setPrivacyData] = useState<any>(null);

  // Check document referrer for tracking
  useEffect(() => {
    if (!queryParams.get('ref') && document.referrer) {
      try {
        const refererUrl = new URL(document.referrer);
        if (refererUrl.hostname !== window.location.hostname) {
          const newParams = new URLSearchParams(location.search);
          newParams.set('ref', 'website-referer');
          newParams.set('from_domain', refererUrl.hostname);
          const newUrl = `${location.pathname}?${newParams.toString()}`;
          navigate(newUrl, { replace: true });
        }
      } catch (e) {
        console.error('Error parsing referrer:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Fetch privacy policy
  useEffect(() => {
    const fetchPrivacyData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/public/privacy-policy`);

        if (response.data.success && response.data.policy) {
          setPrivacyData({
            version: response.data.policy.version,
            validFrom: response.data.policy.validFrom,
            content: response.data.policy.content
          });
        }
      } catch (err) {
        console.error('Error fetching privacy policy:', err);
      }
    };

    fetchPrivacyData();
  }, [API_URL]);

  // Password strength check
  const checkPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    setPasswordStrength(strength);
    return strength >= 3;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setForm({ ...form, password: newPassword });
    checkPasswordStrength(newPassword);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (form.password !== form.confirmPassword) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }

    // Validate password strength
    if (!checkPasswordStrength(form.password)) {
      setError(
        'Passwort muss mindestens 8 Zeichen lang sein und 3 der folgenden Kriterien erfüllen: Großbuchstabe, Zahl, Sonderzeichen.'
      );
      return;
    }

    // Validate privacy consent
    if (!form.privacyConsent) {
      setError('Bitte stimmen Sie der Datenschutzerklärung zu.');
      return;
    }

    try {
      // Direkt beim Backend registrieren (ohne Discovery Service)
      console.log('[Register] Registriere bei:', API_URL);
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        vorname: form.vorname,
        name: form.name,
        geb_am: form.geb_am,
        gender: form.gender,
        strasse: form.strasse,
        plz: form.plz,
        ort: form.ort,
        email: form.email,
        password: form.password,
        privacyConsent: form.privacyConsent,
        referralSource: referralSource,
        fromDomain: fromDomain,
        campaign: campaign,
        club_id: clubId,
      });

      if (response.data.success) {
        // Navigate to verification code page
        navigate('/verify-code', {
          state: { email: response.data.email || form.email },
          replace: true
        });
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.response?.data?.message || 'Ein Fehler ist bei der Registrierung aufgetreten.');
    }
  };

  const getStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return 'bg-error-500';
      case 2:
        return 'bg-warning-500';
      case 3:
        return 'bg-success-600';
      case 4:
        return 'bg-success-500';
      default:
        return 'bg-error-500';
    }
  };

  const formatGermanDate = (dateStr: string) => {
    if (!dateStr) return 'Unbekannt';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0f1419] px-4 py-8">
      <div className="w-full max-w-md lg:max-w-xl">
        <div className="bg-white dark:bg-[#1c2432] rounded-3xl shadow-2xl shadow-gray-200/50 dark:shadow-black/40 border border-gray-100 dark:border-gray-800/50 p-8 sm:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Registrieren
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              Erstelle dein Konto
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <AttributeLabel
                  iconKey="name"
                  tone="primary"
                  className="mb-2"
                  textClassName="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
                >
                  Vorname
                </AttributeLabel>
                <input
                  type="text"
                  placeholder="Max"
                  className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                  value={form.vorname}
                  onChange={(e) => setForm({ ...form, vorname: e.target.value })}
                  required
                />
              </div>
              <div>
                <AttributeLabel
                  iconKey="name"
                  tone="primary"
                  className="mb-2"
                  textClassName="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
                >
                  Nachname
                </AttributeLabel>
                <input
                  type="text"
                  placeholder="Mustermann"
                  className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Birthdate */}
            <div>
              <AttributeLabel
                iconKey="birthday"
                tone="primary"
                className="mb-2"
                textClassName="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
              >
                Geburtsdatum
              </AttributeLabel>
              <input
                type="date"
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                value={form.geb_am}
                onChange={(e) => setForm({ ...form, geb_am: e.target.value })}
                required
              />
            </div>

            {/* Gender */}
            <div>
              <AttributeLabel
                iconKey="gender"
                tone="primary"
                className="mb-2"
                textClassName="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
              >
                Geschlecht
              </AttributeLabel>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: '0', label: 'Männlich' },
                  { value: '1', label: 'Weiblich' },
                  { value: '', label: 'Keine Angabe' },
                ].map((option) => {
                  const active = form.gender === option.value;
                  return (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => setForm({ ...form, gender: option.value })}
                      className={`px-3 py-3 rounded-xl text-sm font-medium transition-all ${
                        active
                          ? 'bg-primary-600 text-white shadow-sm'
                          : 'bg-gray-50 dark:bg-[#0f1419] text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#1a2332]'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Address */}
            <div>
              <AttributeLabel
                iconKey="street"
                tone="primary"
                className="mb-2"
                textClassName="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
              >
                Straße und Hausnummer
              </AttributeLabel>
              <input
                type="text"
                placeholder="Musterstraße 1"
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                value={form.strasse}
                onChange={(e) => setForm({ ...form, strasse: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <AttributeLabel
                  iconKey="address"
                  tone="primary"
                  className="mb-2"
                  textClassName="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
                >
                  PLZ
                </AttributeLabel>
                <input
                  type="text"
                  placeholder="12345"
                  className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                  value={form.plz}
                  onChange={(e) => setForm({ ...form, plz: e.target.value })}
                  pattern="[0-9]{5}"
                  required
                />
              </div>
              <div>
                <AttributeLabel
                  iconKey="city"
                  tone="primary"
                  className="mb-2"
                  textClassName="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
                >
                  Ort
                </AttributeLabel>
                <input
                  type="text"
                  placeholder="Musterstadt"
                  className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                  value={form.ort}
                  onChange={(e) => setForm({ ...form, ort: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <AttributeLabel
                iconKey="email"
                tone="primary"
                className="mb-2"
                textClassName="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
              >
                E-Mail
              </AttributeLabel>
              <input
                type="email"
                placeholder="name@beispiel.de"
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>

            {/* Password */}
            <div>
              <AttributeLabel
                iconKey="password"
                tone="primary"
                className="mb-2"
                textClassName="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
              >
                Passwort
              </AttributeLabel>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all pr-12"
                  value={form.password}
                  onChange={handlePasswordChange}
                  required
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              <div className="mt-2">
                <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${getStrengthColor()}`}
                    style={{ width: `${passwordStrength * 25}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1.5">
                  Min. 8 Zeichen, Großbuchstabe, Zahl, Sonderzeichen
                </p>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <AttributeLabel
                iconKey="password"
                tone="primary"
                className="mb-2"
                textClassName="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
              >
                Passwort bestätigen
              </AttributeLabel>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all pr-12"
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Privacy Consent */}
            <div>
              <label className="flex items-start space-x-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-0 rounded bg-gray-200 dark:bg-gray-700"
                  checked={form.privacyConsent}
                  onChange={(e) => setForm({ ...form, privacyConsent: e.target.checked })}
                  required
                />
                <span className="text-gray-600 dark:text-gray-400">
                  Ich stimme der{' '}
                  <button
                    type="button"
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                    onClick={() => setShowPrivacyModal(true)}
                  >
                    Datenschutzerklärung
                  </button>{' '}
                  zu.
                </span>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-error-50 dark:bg-error-900/20 text-error-600 dark:text-error-400 rounded-xl text-sm">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white py-3.5 rounded-xl font-medium transition-all duration-200 hover:shadow-lg hover:shadow-primary-600/25"
            >
              Registrieren
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white dark:bg-[#1c2432] px-4 text-gray-400 dark:text-gray-500 tracking-wider">
                  oder
                </span>
              </div>
            </div>

            {/* Login Link */}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 py-3.5 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-[#0f1419] transition-all duration-200"
            >
              <UserPlusIcon className="h-5 w-5" />
              Zum Login
            </button>
          </form>
        </div>
      </div>

      {/* Privacy Modal */}
      {showPrivacyModal && privacyData && (
        <ModalShell
          open
          onClose={() => setShowPrivacyModal(false)}
          variant="detail"
          size="lg"
          accent="brand"
          icon={ShieldCheckIcon}
          title="Datenschutzerklärung"
          footer={<ModalActions secondary={{ label: 'Schließen', onClick: () => setShowPrivacyModal(false) }} />}
        >
          <ModalSection>
            {privacyData?.version ? (
              <>
                <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                  Version {privacyData.version}, gültig ab {formatGermanDate(privacyData.validFrom)}
                </p>
                {privacyData.content && (
                  <div
                    className="prose prose-sm text-gray-700 dark:text-gray-300 dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: privacyData.content }}
                  />
                )}
              </>
            ) : (
              <p className="text-gray-600 dark:text-gray-400">
                Keine aktuelle Datenschutzerklärung gefunden.
              </p>
            )}
          </ModalSection>
        </ModalShell>
      )}

      {/* Confirmation Modal */}
      {confirmModal && (
        <ConfirmationModal
          visible={confirmModal.visible}
          onClose={() => setConfirmModal(null)}
          type={confirmModal.type}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          button={confirmModal.button}
          buttons={confirmModal.buttons}
        />
      )}
    </div>
  );
}
